import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../lib/theme';

export interface TotpQrCodeProps {
  /** data:image/svg+xml;base64,... URL from backend (rendered via <img>, no active markup). */
  qrSvg: string;
  secretSetupKey: string;
  onCodeComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
}

/**
 * TOTP setup step: QR code display + manual key + 6-digit confirm input.
 *
 * Security (UX-spec §4, ADR-0003 §1):
 * - Backend delivers QR as data:image/svg+xml;base64 — rendered via <img src>,
 *   so the SVG is treated as an image, not as HTML/script markup.
 *   This is the "Variante 2 (vorzuziehen)" recommended by the security review.
 * - Setup key is masked by default; revealed on demand with 5-second auto-hide.
 */
export function TotpQrCode({
  qrSvg,
  secretSetupKey,
  onCodeComplete,
  disabled = false,
  error = false,
}: TotpQrCodeProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auto-hide the key after 5 seconds
  useEffect(() => {
    if (!revealed) return;
    const timer = setTimeout(() => setRevealed(false), 5000);
    return () => clearTimeout(timer);
  }, [revealed]);

  const handleCopy = () => {
    navigator.clipboard.writeText(secretSetupKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Normalise: if qrSvg does not already have a data: prefix, treat as raw SVG
  // and base64-encode it for safe use in an <img> src attribute.
  const imgSrc = qrSvg.startsWith('data:')
    ? qrSvg
    : `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(qrSvg)))}`;

  const formattedKey = secretSetupKey
    .toUpperCase()
    .replace(/\s/g, '')
    .match(/.{1,4}/g)
    ?.join(' ') ?? secretSetupKey;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* QR code — rendered as <img> so SVG is an image, not executable markup */}
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 200,
          height: 200,
          margin: '0 auto',
          background: '#fff',
          padding: 12,
          borderRadius: 12,
          border: '1px solid #e4e7ec',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <img
            src={imgSrc}
            alt={t('mfa.setup.step_totp.qr_alt')}
            width={176}
            height={176}
            style={{ display: 'block' }}
          />
        </div>
      </div>

      {/* Manual setup key */}
      <div>
        <p style={{ fontSize: 12, color: '#667085', marginBottom: 8 }}>
          {t('mfa.setup.step_totp.manual_key_label')}
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#f9fafb',
          border: '1px solid #e4e7ec',
          borderRadius: 8,
          padding: '9px 12px',
        }}>
          <span style={{
            fontFamily: 'monospace',
            fontSize: 14,
            letterSpacing: 2,
            flex: 1,
            color: '#101828',
          }}>
            {revealed ? formattedKey : '•••• •••• •••• ••••'}
          </span>
          <button
            type="button"
            onClick={() => setRevealed(true)}
            style={smallBtnStyle}
            title={t('common.action.show')}
          >
            {t('common.action.show')}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            style={smallBtnStyle}
            title={t('common.action.copy')}
          >
            {copied ? '✓' : t('common.action.copy')}
          </button>
        </div>
      </div>

      {/* 6-digit confirm input */}
      <div>
        <p style={{ fontSize: 13, color: '#344054', fontWeight: 500, marginBottom: 10 }}>
          {t('mfa.setup.step_totp.confirm_label')}
        </p>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          disabled={disabled}
          aria-invalid={error}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            e.target.value = val;
            if (val.length === 6) onCodeComplete(val);
          }}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: 12,
            textAlign: 'center',
            padding: '12px 16px',
            border: `2px solid ${error ? '#f04438' : '#e4e7ec'}`,
            borderRadius: 10,
            background: '#fff',
            color: '#101828',
            outline: 'none',
            transition: 'border-color 0.15s',
            boxShadow: error ? '0 0 0 3px rgba(240,68,56,0.15)' : undefined,
          }}
        />
        {error && (
          <p style={{ fontSize: 12, color: '#f04438', marginTop: 5 }}>
            {t('mfa.setup.step_confirm.invalid_code')}
          </p>
        )}
      </div>
    </div>
  );
}

const smallBtnStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: BRAND,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
};
